// Workload Intelligence — data hooks for the Work → Workload tab. Thin
// wrappers over workloadService: each hook owns one fetch (with cancel-on-
// unmount) and exposes `{ data, loading, error, reload }`. Errors are kept raw
// so the page can toast the backend message (backend-error-surfacing rule) —
// notably the 403 `message` from /workload/people when the caller has no
// workload visibility beyond self.
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  type AttentionPage, type AttentionParams, type CapacityPage, type CapacityParams, type MyWorkload, type PersonWorkload,
  type SuggestionsParams, type TrendsParams, type WorkloadDepartmentRow, type WorkloadPeoplePage, type WorkloadPeopleParams,
  type WorkloadScopeSummary, type WorkloadSuggestions, type WorkloadTrends,
  getMyWorkload, getPersonWorkload, getWorkloadSuggestions, getWorkloadTrends, listAttention, listCapacity, listWorkloadDepartments,
  listWorkloadPeople, getWorkloadSummary,
} from '@/services/workloadService';

export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: unknown;
  reload: () => Promise<void>;
}

/** Generic one-shot fetch keyed on `key` (a JSON of the params). `enabled`
 * false skips the call and leaves `data` null (e.g. Team before the policy
 * says the caller may see it). */
function useFetch<T>(fn: () => Promise<T>, key: string, enabled = true): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<unknown>(null);
  const alive = useRef(true);
  const fnRef = useRef(fn);
  fnRef.current = fn;

  const run = useCallback(async () => {
    if (!enabled) { setLoading(false); return; }
    setLoading(true);
    try {
      const d = await fnRef.current();
      if (alive.current) { setData(d); setError(null); }
    } catch (e) {
      if (alive.current) { setError(e); setData(null); }
    } finally {
      if (alive.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled]);

  useEffect(() => {
    alive.current = true;
    void run();
    return () => { alive.current = false; };
  }, [run]);

  return { data, loading, error, reload: run };
}

export const useMyWorkload = (enabled = true, refreshKey = 0): AsyncState<MyWorkload> =>
  useFetch(getMyWorkload, `me:${refreshKey}`, enabled);

export const useWorkloadSummary = (params: { department_id?: string } = {}, enabled = true, refreshKey = 0): AsyncState<WorkloadScopeSummary> =>
  useFetch(() => getWorkloadSummary(params), `summary:${JSON.stringify(params)}:${refreshKey}`, enabled);

export const useWorkloadPeople = (params: WorkloadPeopleParams = {}, enabled = true, refreshKey = 0): AsyncState<WorkloadPeoplePage> =>
  useFetch(() => listWorkloadPeople(params), `people:${JSON.stringify(params)}:${refreshKey}`, enabled);

export const useWorkloadDepartments = (enabled = true, refreshKey = 0): AsyncState<WorkloadDepartmentRow[]> =>
  useFetch(listWorkloadDepartments, `departments:${refreshKey}`, enabled);

export const useWorkloadAttention = (params: AttentionParams = {}, enabled = true, refreshKey = 0): AsyncState<AttentionPage> =>
  useFetch(() => listAttention(params), `attention:${JSON.stringify(params)}:${refreshKey}`, enabled);

export const usePersonWorkload = (employeeId: string | null): AsyncState<PersonWorkload> =>
  useFetch(() => getPersonWorkload(employeeId as string), `person:${employeeId ?? ''}`, !!employeeId);

/** Admin capacity table (work:manage) — only people with capacity configured. */
export const useWorkloadCapacity = (params: CapacityParams = {}, enabled = true, refreshKey = 0): AsyncState<CapacityPage> =>
  useFetch(() => listCapacity(params), `capacity:${JSON.stringify(params)}:${refreshKey}`, enabled);

/** Phase 3 — 30-day snapshot series for one person or the viewer's population. */
export const useWorkloadTrends = (params: TrendsParams = {}, enabled = true, refreshKey = 0): AsyncState<WorkloadTrends> =>
  useFetch(() => getWorkloadTrends(params), `trends:${JSON.stringify(params)}:${refreshKey}`, enabled);

/** Phase 3 — suggested moves (team reach; the panel hides itself on 403). */
export const useWorkloadSuggestions = (params: SuggestionsParams = {}, enabled = true, refreshKey = 0): AsyncState<WorkloadSuggestions> =>
  useFetch(() => getWorkloadSuggestions(params), `suggestions:${JSON.stringify(params)}:${refreshKey}`, enabled);
